import { useEffect, useState } from 'react';
import { Storage } from '../lib/storage';
import {
  readBookingConfig,
  readBookingGuestPrices,
  readBookingZones,
} from '../lib/bookingConfigRead';
import { playerSurfaceActive } from '../lib/api/customerContext';
import { loadEngineSettings } from '../lib/api/engineSettings';
import {
  fetchEloAdjustments,
  fetchPlayers,
  persistPlayers,
  playerCommandsAreServerAuthoritative,
} from '../lib/api/players';
import {
  fetchSingleMatches,
  persistSingleMatches,
  singleMatchesAreServerAuthoritative,
  fetchPlannedMatches,
} from '../lib/api/matches';
import { fetchActivities } from '../lib/api/activities';
import { fetchBookings } from '../lib/api/bookings';
import { fetchAssets } from '../lib/api/assets';
import { fetchAssetBookings } from '../lib/api/assetBookings';
import { endScopeTransition } from '../lib/api/scopeTransition';

type AnySetter = (value: any) => void;
type AnyRef = { current: any };

/**
 * The atomic cross-slice mount loader, lifted byte-for-byte out of
 * Padel24AppMain (extracted from the former monolith) into the state layer
 * (PR-AF, AppShell step 3).
 *
 * It reads every persisted key from Storage ONCE on mount, runs the one-time
 * data migrations, and flushes the results through the eight state hooks'
 * setters/refs (passed in via `deps`) — then flips each slice's loaded-flag so
 * the per-slice blanket-persist effects (§13.20 R1) start saving. The body is
 * UNCHANGED from the original; only the surrounding hook wrapper + the `deps`
 * destructure were added.
 *
 * R1/§13.20: the loader is async, so its setter-flushes + flag-flips happen
 * after the initial synchronous effect pass — the persist effects therefore
 * skip on mount (flags still false) and only persist once a flag is true with
 * the loaded value. `setTournament(activeT)` and `activeTournLoaded.current =
 * true` are adjacent (no await between), so a flag can never be true with empty
 * state. Mount-once (`[]` deps) is deliberate; do not add deps. StrictMode is
 * OFF (R2). `Padel24Engine` is the live engine object, passed in as
 * a prop because the migrations call `Padel24Engine.playerDB.base()` /
 * `.settings.apply()` / `.booking.DEFAULT_*`.
 */
export interface InitialLoadDeps {
  // setters/refs from the eight state hooks (typed loosely — they are plumbing,
  // and each hook's concrete Dispatch/MutableRefObject is assignable to these).
  setTournament: AnySetter;
  setActiveSeries: AnySetter;
  setActivities: AnySetter;
  setAllPlayers: AnySetter;
  setArchive: AnySetter;
  setArchiveLoaded: AnySetter;
  setBanepadLockedCourt: AnySetter;
  /**
   * The held config (the merge over the engine defaults) and, second, the
   * document as STORED — the base a save merges into (#1357).
   */
  setBookingConfig: (config: any, stored?: Record<string, unknown> | null) => void;
  /** Whether the STORED booking config has opening hours (#1192). Optional, like the asset slices. */
  setBookingHoursSaved?: AnySetter;
  /**
   * The stored zones and, second, whether the read ANSWERED (#1362): false
   * after a failed read, and the slice then refuses to save them.
   */
  setBookingZones: (zones: any, read?: boolean) => void;
  /**
   * The venue's guest default price document (#833), and whether the read
   * answered (#1362). Optional, like the asset slices.
   */
  setBookingGuestPrices?: (guestPrices: any, read?: boolean) => void;
  setBookings: AnySetter;
  /**
   * Equipment beside the courts (docs/domains/ASSET_BOOKING.md). OPTIONAL,
   * because the loader is also driven by tests that assemble their own deps
   * for the slices they are about; a loader that demanded every setter would
   * make each new slice a change to every one of them.
   */
  setAssets?: AnySetter;
  setAssetBookings?: AnySetter;
  setEloAdjustments: AnySetter;
  setPausedSeries: AnySetter;
  setPausedTournaments: AnySetter;
  setSingleMatches: AnySetter;
  setPlannedMatches: AnySetter;
  activeSeriesLoaded: AnyRef;
  activeTournLoaded: AnyRef;
  bookingsRef: AnyRef;
  pausedLoaded: AnyRef;
  pausedSeriesLoaded: AnyRef;
  // The live Padel24Engine object (typed loosely — it is the
  // runtime engine, extracted from the former monolith into src/).
  Padel24Engine: any;
}

export function useInitialLoad(deps: InitialLoadDeps) {
  // THE MEMBER APP DOES NOT COME THROUGH HERE (#185).
  //
  // Every read below is a `/kv` key, which is CLUB-scoped and needs a club
  // user's session. A member has neither, so in member mode this loader would
  // fire twenty-odd 401s — and the transport treats a 401 on an authenticated
  // request as the session ending, so it would also sign the member out while
  // loading. `useMemberBootstrap` populates the same slices from one
  // member-scoped request instead.
  //
  // Read once, before any effect: the backend and the session do not change
  // under a running page.
  // ── THE PLAYER SURFACE, WHICHEVER CREDENTIAL PROVED IT (#800) ─────
  //
  // Every read below is a `/kv` key, which is CLUB-scoped: a player holds no
  // club user, so firing them answers 401 — and the transport treats a 401 on
  // an authenticated request as the session ending, so it would sign the
  // player out while loading. That was true for a legacy member and is equally
  // true for an account-only player, and asking `memberModeActive()` here saw
  // only the first of them.
  //
  // It is the SURFACE that decides, not the family: `playerSurfaceActive()`
  // is false in the club portal — where these reads are exactly right — and
  // true on the player surface with either credential.
  const [memberMode] = useState(() => playerSurfaceActive());
  const {
    setTournament,
    setActiveSeries,
    setActivities,
    setAllPlayers,
    setArchive,
    setArchiveLoaded,
    setBanepadLockedCourt,
    setBookingConfig,
    setBookingHoursSaved,
    setBookingZones,
    setBookingGuestPrices,
    setBookings,
    setAssets,
    setAssetBookings,
    setEloAdjustments,
    setPausedSeries,
    setPausedTournaments,
    setSingleMatches,
    setPlannedMatches,
    activeSeriesLoaded,
    activeTournLoaded,
    bookingsRef,
    pausedLoaded,
    pausedSeriesLoaded,
    Padel24Engine,
  } = deps;

  // Load archive + custom players from storage at startup
  // useEffect to avoid storage access blocking the first render
  useEffect(() => {
    // See the note at the top of this function: in member mode the whole body
    // below is the wrong data source, and firing it would sign the member out.
    if (memberMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Storage is now always available (window.storage, localStorage, or in-memory)
        // Archive — list all keys with prefix
        let keys = [];
        try {
          const list = await Storage.list('tournament:');
          keys = (list && list.keys) || [];
          console.log(
            '[Padel24] Storage list returnerte',
            keys.length,
            'arkiverte turneringer (' + Storage.backend + ')',
          );
        } catch (e) {
          console.error('[Padel24] Storage list feilet:', e);
        }

        const items = [];
        for (const key of keys) {
          try {
            const res = await Storage.get(key);
            if (res && res.value) {
              const parsed = JSON.parse(res.value);
              items.push({ key, data: parsed });
            }
          } catch (e) {
            console.warn('[Padel24] Kunne ikke lese', key, ':', e);
          }
        }
        items.sort((a, b) => (b.data.archivedAt || 0) - (a.data.archivedAt || 0));

        // Engine settings — load via the API (falls back to Storage on non-http
        // backends) and apply to the engine. Affects new ELO calcs immediately.
        try {
          const es = await loadEngineSettings();
          if (es) {
            Padel24Engine.settings.apply(es);
            console.log(
              '[Padel24] Lastet engine-innstillinger:',
              Padel24Engine.settings.serialize(),
            );
          }
        } catch (e) {
          console.warn('[Padel24] Kunne ikke laste engine-innstillinger:', e);
        }

        // Player database — load all players (system + custom) from Storage
        // Migrate PLAYER_DB to Storage on first-time use
        let playersList = [];
        // Which world we are in. The blob path still seeds and migrates from
        // the browser, unchanged; the server path does neither (#272).
        const playersCommandBackend = playerCommandsAreServerAuthoritative();
        try {
          playersList = await fetchPlayers();
          if (playersList.length) {
            console.log('[Padel24] Lastet', playersList.length, 'spillere');
          }

          // NO SEEDING, NO TOP-UP, NO MIGRATION ON THE SERVER PATH (#272).
          //
          // Everything below runs DURING PAGE LOAD and writes the GLOBAL player
          // list — a table with no tenant column. Seeding a shared player bank
          // from whichever browser happens to boot first is not a client's job
          // in the first place, and it was the trigger for the worst defect on
          // this issue: `fetchPlayers` used to answer `[]` for a failed read,
          // this branch read that as «not migrated yet», and the resulting
          // whole-collection PUT deleted every real player on the platform.
          //
          // The read now throws rather than lying, and the writes are gone. A
          // fresh environment is seeded by `pnpm --filter @starpoint/db exec tsx
          // scripts/seed-demo-players.ts`; the two one-time migrations below
          // are `backfill-players.ts`, which already folds `custom_players_v1`
          // in and runs once per environment against the real database.
          if (playersCommandBackend) {
            if (playersList.length === 0) {
              console.log('[Padel24] Ingen spillere i databasen — seed med db-skriptet.');
            }
          } else if (playersList.length === 0) {
            playersList = Padel24Engine.playerDB.base().map((p) => ({
              ...p,
              isSystem: true,
            }));
            console.log('[Padel24] Migrerer', playersList.length, 'system-spillere');
            await persistPlayers(playersList);
          } else {
            // TOP-UP: if there are new system players in PLAYER_DB that are not
            // in playersList (e.g. because we increased the demo-player bank from
            // 80 to 200), add them. Identifies players by id and
            // skips the ones that already exist.
            // Blob path only — see the note above.
            const existingIds = new Set(playersList.map((p) => p.id));
            const baseDB = Padel24Engine.playerDB.base();
            const missing = baseDB.filter((p) => !existingIds.has(p.id));
            if (missing.length > 0) {
              const newSystemPlayers = missing.map((p) => ({ ...p, isSystem: true }));
              playersList = [...playersList, ...newSystemPlayers];
              console.log(
                '[Padel24] Top-up:',
                missing.length,
                'nye system-spillere lagt til (total nå:',
                playersList.length,
                ')',
              );
              await persistPlayers(playersList);
            }
          }
        } catch (e) {
          console.warn('[Padel24] Kunne ikke laste spillere:', e);
          // Fall back to the in-memory PLAYER_DB — AND NOTHING IS STORED.
          //
          // This branch now also catches a REFUSED read (a 401, a 502): since
          // #272 `fetchPlayers` throws on a non-2xx instead of answering `[]`,
          // so a failed read lands here rather than being mistaken for an empty
          // database. «Not stored» is the load-bearing half — the app runs on
          // demo players for this session, and writes none of them anywhere.
          //
          // NOT ON THE API (#1060). There the bank's ids 1..200 are real rows
          // in the platform table, so «demo players for this session» would be
          // 200 platform players this club was never shown — pickable, and
          // rated on archive. An unreadable list stays empty there.
          playersList = playersCommandBackend
            ? []
            : Padel24Engine.playerDB.base().map((p) => ({ ...p, isSystem: true }));
        }

        // Migrate old custom_players_v1 data if it exists (one-time)
        // Merge with the existing playersList.
        // BLOB PATH ONLY (#272): on the server path `backfill-players.ts` has
        // already folded this key into the relational table, once, against the
        // real database — not from a browser, on every load.
        try {
          const cp = playersCommandBackend ? null : await Storage.get('custom_players_v1');
          if (cp && cp.value) {
            const oldCustom = JSON.parse(cp.value) || [];
            if (oldCustom.length > 0) {
              const existingIds = new Set(playersList.map((p) => p.id));
              const toAdd = oldCustom
                .filter((p) => !existingIds.has(p.id))
                .map((p) => ({ ...p, isSystem: false }));
              if (toAdd.length > 0) {
                playersList = [...playersList, ...toAdd];
                await persistPlayers(playersList);
                console.log('[Padel24] Migrerte', toAdd.length, 'custom-spillere til ny database');
              }
            }
            // Delete the old key after a successful migration
            await Storage.delete('custom_players_v1');
          }
        } catch (_e) {
          // No custom players to migrate — that's fine
        }

        // Single matches (outside of tournaments)
        let singleMatchesList = [];
        try {
          singleMatchesList = await fetchSingleMatches();
          console.log(
            '[Padel24] Lastet',
            singleMatchesList.length,
            'enkeltkamper (' + Storage.backend + ')',
          );
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av enkeltkamper:', e);
        }

        // MIGRATION: old single matches (stored before v1.7.0's newbieMul storage)
        // are missing t1NewbieMul/t2NewbieMul on result. We can reconstruct them
        // by comparing t1Change with what it would have been WITHOUT the boost —
        // using the ELO-before numbers and known formulas.
        //
        // Formula: t1Change = K * mov * newbieMul * (actual - expected)
        // We know t1Change, K, mov, actual, expected → newbieMul = t1Change / (K * mov * (actual - expected))
        //
        // To avoid recomputing every time, we set a migration flag on
        // each match so that we only migrate once.
        let smNeedsResave = false;
        singleMatchesList = singleMatchesList.map((m) => {
          if (!m.result || m.result.t1NewbieMul != null) return m;
          // Missing newbieMul → reconstruct from the ELO formula
          const t1Elo = m.result.t1EloBefore;
          const t2Elo = m.result.t2EloBefore;
          if (t1Elo == null || t2Elo == null) return m;

          const K = m.kFactor || 32;
          const mov = m.result.mov || 1;
          const expected1 = 1 / (1 + Math.pow(10, (t2Elo - t1Elo) / 400));
          const expected2 = 1 - expected1;
          const winner = m.result.winner;
          const actual1 = winner === 'team1' ? 1 : 0;
          const actual2 = 1 - actual1;

          // Compute the expected change WITHOUT the boost
          const baseT1 = K * mov * (actual1 - expected1);
          const baseT2 = K * mov * (actual2 - expected2);

          // newbieMul = actual change / expected without boost. Robust against
          // division by zero (only a theoretical possibility at perfect parity).
          const t1NewbieMul = Math.abs(baseT1) > 0.1 ? Math.abs(m.result.t1Change / baseT1) : 1.0;
          const t2NewbieMul = Math.abs(baseT2) > 0.1 ? Math.abs(m.result.t2Change / baseT2) : 1.0;

          // Clamp between 1.0 and 2.0 (valid boost values), round to 2 decimals
          const safeT1Mul = Math.max(1.0, Math.min(2.0, Math.round(t1NewbieMul * 100) / 100));
          const safeT2Mul = Math.max(1.0, Math.min(2.0, Math.round(t2NewbieMul * 100) / 100));

          smNeedsResave = true;
          return {
            ...m,
            result: {
              ...m.result,
              t1NewbieMul: safeT1Mul,
              t2NewbieMul: safeT2Mul,
            },
          };
        });
        if (smNeedsResave) {
          console.log('[Padel24] Migrerte newbieMul på gamle enkeltkamper');
          // NOT WRITTEN BACK on the API backend (#245). This is a whole-list
          // resave, and that door is closed: it replaced the table from a list
          // read earlier, which is how a portal save erased matches the pad had
          // recorded. The values are still applied IN MEMORY above, so the
          // history view shows them; making them permanent for rows that
          // predate the field is a one-off backfill against the database, not
          // something every client should attempt on every load.
          if (!singleMatchesAreServerAuthoritative()) {
            try {
              await persistSingleMatches(singleMatchesList);
            } catch {
              /* ignore write errors */
            }
          }
        }

        // Planned (not-yet-played) single matches created from a court booking.
        let plannedMatchesList = [];
        try {
          plannedMatchesList = await fetchPlannedMatches();
          if (plannedMatchesList.length) {
            console.log(
              '[Padel24] Lastet',
              plannedMatchesList.length,
              'planlagte kamper (' + Storage.backend + ')',
            );
          }
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av planlagte kamper:', e);
        }

        // Paused tournaments (parked for later)
        let pausedList = [];
        let pausedRead = false;
        try {
          const pt = await Storage.read('paused_tournaments_v1');
          pausedRead = pt.status !== 'failed';
          if (pt.status === 'ok' && pt.value) {
            pausedList = JSON.parse(pt.value) || [];
            // Ensure all paused tournaments have a unique id (for older data without an id)
            let needsResave = false;
            pausedList = pausedList.map((t) => {
              if (!t.id) {
                needsResave = true;
                return { ...t, id: `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}` };
              }
              return t;
            });
            if (needsResave) {
              try {
                await Storage.set('paused_tournaments_v1', JSON.stringify(pausedList));
              } catch {
                /* ignore write errors */
              }
            }
            console.log('[Padel24] Lastet', pausedList.length, 'pauste turneringer fra Storage');
          }
        } catch (e) {
          // RESET, like the other three slices (#413). A read that SUCCEEDED
          // and then failed to parse or normalise is not a trustworthy answer
          // either — and this slice is the worst place to get that wrong,
          // because `pausedList` is still `[]` here, so arming the flag would
          // persist an empty list over a document it could not read.
          console.error('[Padel24] Feil ved lasting av pauste turneringer:', e);
          pausedRead = false;
        }

        // Active/ongoing tournament — loaded so an ongoing KM/Padel24 survives a reload.
        let activeTournRead = false;
        try {
          const at = await Storage.read('active_tournament_v1');
          activeTournRead = at.status !== 'failed';
          if (at.status === 'ok' && at.value) {
            const activeT = JSON.parse(at.value);
            if (activeT && activeT.type) {
              if (!cancelled) setTournament(activeT);
              console.log(
                '[Padel24] Lastet aktiv turnering:',
                activeT.config?.name || activeT.name,
              );
            }
          }
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av aktiv turnering:', e);
          activeTournRead = false;
        }
        // ── ARMED ONLY IF THE READ ANSWERED (#413) ──────────────────────
        //
        // `Storage.get` returns null both for "absent" and for "the request
        // failed", so a restarting API used to look exactly like an empty
        // club — and the blanket-persist flag was armed anyway, leaving the
        // app able to write that emptiness over a document still in the
        // database. `read` distinguishes them; a failed read leaves the flag
        // FALSE, so this slice cannot be persisted from a state it never
        // actually loaded.
        if (activeTournRead) activeTournLoaded.current = true;
        else console.error('[Padel24] Aktiv turnering kunne IKKE leses — lagring er av.');

        // Load the active series (its own track — not parked by other tournaments)
        // BUG-FIX: Storage.get returns {value: '...'} (same pattern as
        // single_matches_v1 and players_v1), not a string directly. Previously
        // we tried to parse seriesData directly — which gave an undefined .type and
        // thus silently "lost" the series on every reload. Now we read .value.
        let seriesRead = false;
        try {
          const sd = await Storage.read('active_series_v1');
          seriesRead = sd.status !== 'failed';
          if (sd.status === 'ok' && sd.value) {
            const series = JSON.parse(sd.value);
            if (series && series.type === 'seriespill') {
              if (!cancelled) setActiveSeries(series);
              console.log('[Padel24] Lastet aktiv serie:', series.config?.name || series.name);
            }
          } else {
            console.log('[Padel24] Ingen aktiv serie i Storage');
          }
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av aktiv serie:', e);
          seriesRead = false;
        }
        // Only if the read answered — see #413 above.
        if (seriesRead) activeSeriesLoaded.current = true;
        else console.error('[Padel24] Aktiv serie kunne IKKE leses — lagring er av.');

        // BANEPAD: load which court this device is locked to (kiosk mode).
        // Per-device (local Storage) — does not sync across devices until a backend exists.
        try {
          const bd = await Storage.get('banepad_device_v1');
          if (bd && bd.value) {
            const parsed = JSON.parse(bd.value);
            if (parsed && parsed.lockedCourt != null && !cancelled) {
              setBanepadLockedCourt(Number(parsed.lockedCourt));
            }
          }
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av banepad-enhet:', e);
        }

        // Load paused series
        let pausedSeriesRead = false;
        try {
          const ps = await Storage.read('paused_series_v1');
          pausedSeriesRead = ps.status !== 'failed';
          if (ps.status === 'ok' && ps.value) {
            const list = JSON.parse(ps.value);
            if (Array.isArray(list) && !cancelled) {
              setPausedSeries(list);
              console.log('[Padel24] Lastet', list.length, 'pausede serier');
            }
          }
        } catch (e) {
          console.error('[Padel24] Feil ved lasting av pausede serier:', e);
          pausedSeriesRead = false;
        }
        // Only if the read answered — see #413 above.
        if (pausedSeriesRead) pausedSeriesLoaded.current = true;
        else console.error('[Padel24] Pausede serier kunne IKKE leses — lagring er av.');

        if (!cancelled) {
          // MIGRATION v1.7: Count the actual matches for each player from the archive +
          // single matches and set matchesPlayed/lastMatchAt retroactively.
          // Players without a matchesPlayed field get this computed once.
          // After the first computation the count will be updated automatically on
          // every applyEloUpdate call in future matches.
          // BLOB PATH ONLY (#272). The relational table has `matches_played` as
          // a column with a value for every row, so there is nothing to
          // backfill; running it would rewrite the global list from a browser.
          const needsMigration =
            !playersCommandBackend && playersList.some((p) => p.matchesPlayed === undefined);
          if (needsMigration) {
            console.log('[Padel24] Migrerer matchesPlayed/lastMatchAt fra arkiv...');
            const counts = new Map(); // playerId → { count, lastAt }

            // Helper function: register a match for four players
            const recordPlayedMatch = (playerIds, ts) => {
              playerIds.forEach((id) => {
                if (!id) return;
                const curr = counts.get(id) || { count: 0, lastAt: 0 };
                curr.count += 1;
                if (ts && ts > curr.lastAt) curr.lastAt = ts;
                counts.set(id, curr);
              });
            };

            // Count from the ARCHIVE (finished tournaments)
            items.forEach((archived) => {
              const t = archived.tournament || archived;
              const matches = t.matches || [];
              const ts = archived.archivedAt || t.createdAt || archived.createdAt || 0;
              matches.forEach((m) => {
                if (!m.completed) return;
                const t1 = [m.team1?.p1, m.team1?.p2].filter(
                  (s) => s?.kind === 'concrete' || typeof s === 'string',
                );
                const t2 = [m.team2?.p1, m.team2?.p2].filter(
                  (s) => s?.kind === 'concrete' || typeof s === 'string',
                );
                const ids = [
                  ...t1.map((s) => s.playerId || s),
                  ...t2.map((s) => s.playerId || s),
                  // Americano format with p1Id/p2Id directly
                  m.team1?.p1Id,
                  m.team1?.p2Id,
                  m.team2?.p1Id,
                  m.team2?.p2Id,
                ].filter(Boolean);
                recordPlayedMatch(ids, ts);
              });
            });

            // Count from SINGLE MATCHES
            singleMatchesList.forEach((m) => {
              const ids = [m.team1?.p1Id, m.team1?.p2Id, m.team2?.p1Id, m.team2?.p2Id].filter(
                Boolean,
              );
              const ts = m.playedAt || m.createdAt || 0;
              recordPlayedMatch(ids, ts);
            });

            // Apply the counts to the player list. Players who have not played
            // get matchesPlayed=0 (they are beginners — correct behavior).
            playersList = playersList.map((p) => {
              if (p.matchesPlayed !== undefined) return p; // already migrated
              const c = counts.get(p.id);
              return {
                ...p,
                matchesPlayed: c?.count || 0,
                lastMatchAt: c?.lastAt || null,
              };
            });

            try {
              await persistPlayers(playersList);
            } catch {
              /* ignore write errors */
            }
            console.log(
              '[Padel24] Migrering ferdig. Spillere med kamper:',
              playersList.filter((p) => p.matchesPlayed > 0).length,
              '/ totalt:',
              playersList.length,
            );
          }

          // Manual ELO adjustments (outside of matches)
          let eloAdjustmentsList = [];
          try {
            eloAdjustmentsList = await fetchEloAdjustments();
          } catch (e) {
            console.error('[Padel24] Feil ved lasting av ELO-justeringer:', e);
          }

          // Court bookings
          let bookingsList = [];
          try {
            bookingsList = await fetchBookings();
          } catch (e) {
            console.error('[Padel24] Feil ved lasting av bookinger:', e);
          }
          // Equipment and its bookings (docs/domains/ASSET_BOOKING.md). Two
          // reads that may each fail on their own: a venue with no equipment
          // still has a calendar to show.
          let assetsList: unknown[] = [];
          let assetBookingsList: unknown[] = [];
          try {
            assetsList = await fetchAssets();
          } catch (e) {
            console.error('[Padel24] Feil ved lasting av utstyr:', e);
          }
          try {
            assetBookingsList = await fetchAssetBookings();
          } catch (e) {
            console.error('[Padel24] Feil ved lasting av utstyrsbookinger:', e);
          }
          // ACTIVITIES: load tournaments awaiting registration
          let activitiesList = [];
          try {
            activitiesList = (await fetchActivities()) || [];
          } catch (e) {
            console.error('[Padel24] Feil ved lasting av aktiviteter:', e);
          }
          // Price zones — WHAT IS STORED, and nothing else (#1312, owner
          // decision 2026-11-03: no price until zones exist). No saved zone is
          // an empty list, which the shared engine (`courtPricesSet`) reads as
          // «Pris ikke satt». The guest default price (#833) — an object or
          // null; never a default, for the reason the zones have none.
          //
          // Read through `Storage.read` (lib/bookingConfigRead.ts), never
          // `get`: a FAILED read is not «nothing stored» (#1362). It leaves no
          // version token, so a save would go out with no precondition and
          // erase the club's real prices. The slice is told which documents
          // answered, and refuses to save the others until a read does.
          const zonesRead = await readBookingZones();
          const guestRead = await readBookingGuestPrices();
          // Booking configuration — the stored document, the merge over the
          // engine defaults, and whether hours are saved, read the one way the
          // slice's re-read also uses (lib/bookingConfigRead.ts): a FAILED read
          // is unknown and nothing is claimed (#413, #1192); the stored document
          // is the base a save merges into (#1357), and null blocks saving
          // until a read answers (#1359).
          const {
            config: configObj,
            stored: storedConfig,
            hoursSaved,
          } = await readBookingConfig(Padel24Engine.booking.DEFAULT_CONFIG);

          setArchive(items);
          setAllPlayers(playersList);
          setSingleMatches(singleMatchesList);
          setPlannedMatches(plannedMatchesList);
          setEloAdjustments(eloAdjustmentsList);
          setPausedTournaments(pausedList);
          // Only if the read answered — see #413 above.
          if (pausedRead) pausedLoaded.current = true;
          else console.error('[Padel24] Pausede turneringer kunne IKKE leses — lagring er av.');
          setBookings(bookingsList);
          bookingsRef.current = bookingsList;
          setAssets?.(assetsList);
          setAssetBookings?.(assetBookingsList);
          setActivities(Array.isArray(activitiesList) ? activitiesList : []);
          setBookingZones(zonesRead.zones, zonesRead.read);
          setBookingGuestPrices?.(guestRead.guestPrices, guestRead.read);
          setBookingConfig(configObj, storedConfig);
          setBookingHoursSaved?.(hoursSaved);
          setArchiveLoaded(true);
          // THE NEW SCOPE'S STORE HAS ITS DATA: a scope switch that was in
          // progress is over, and writes may flow again
          // (lib/api/scopeTransition.ts). A no-op when none was.
          endScopeTransition();
          console.log('[Padel24] STATE etter init:', {
            archive: items.length,
            allPlayers: playersList.length,
            singleMatches: singleMatchesList.length,
            paused: pausedList.length,
          });
        }
      } catch (e) {
        console.error('[Padel24] Storage-load feilet:', e);
        if (!cancelled) {
          setArchiveLoaded(true);
          // A failed load is still the end of the transition: the portal is
          // on the new scope, and keeping it closed would help nobody.
          endScopeTransition();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
